<template>
    <base-dialog :loading="loading" :value="dialog" :width="600" :fullscreen="isMobile" :title="title" @input="val => { if (!val) hide() }" @close="hide">
        <skeleton-loader v-if="loadingOptions" type="rows" :count="6" />
        <div v-else class="ques-body">
            <kimsa-text-field v-if="!readonly" :label="$t('common.name')" :value="name" @change="name = $event"
                :placeholder="$t('common.write_here')" dense />
            <kimsa-tags-picker v-if="!readonly" ref="tagsPicker" @change="tags = $event" :default-tags="tags"
                :label="$t('exercises.tags')" force-value />
            <div>
                <label class="ques-label">{{ $t('questionnaires.questions') }}</label>
                <draggable v-model="questionOptions" :move="onMove" handle=".q-drag" class="q-list"
                    ghost-class="q-ghost" chosen-class="q-chosen" drag-class="q-dragging" :animation="180">
                    <div class="q-row" v-for="(question, i) in questionOptions" :key="question.id || question.title"
                        :class="{ 'q-row--fixed': question.force }" @mouseover="hoverIndex = i"
                        @mouseleave="hoverIndex = null">
                        <span class="q-drag" :class="{ hidden: question.force }">
                            <v-icon size="18">mdi-drag-vertical</v-icon>
                        </span>
                        <span class="q-text">{{ question.title }}</span>
                        <button v-if="question.coachId && hoverIndex === i" type="button" class="q-del"
                            @click="showDeleteQuestion(question, i)">
                            <v-icon size="14">mdi-delete-outline</v-icon>
                        </button>
                        <kimsa-toggle :value="question.added" :disabled="question.force"
                            @change="question.added = $event" />
                    </div>
                </draggable>
                <v-btn v-if="!readonly" icon class="q-add" @click="showAddQuestion">
                    <v-icon size="20">mdi-plus</v-icon>
                </v-btn>
            </div>
        </div>

        <template v-slot:footer v-if="!readonly">
            <dialog-actions hide-cancel :confirm-label="$t('common.save')" :disabled="disabledAdd" :loading="loading"
                @confirm="save" />
        </template>

        <create-question-dialog ref="createQuestion" isSecondDialog :coachId="coachId" @save="onSaveQuestion" />
        <kimsa-confirm ref="showDelete" :title="$t('questions.delete_title')"
            :content="$t('questions.confirm_deleted')" :persistent="false" :loading="loadingDelete"
            @confirm="onDeleteQuestion" @cancel="deleteQuestion = null" :close-on-confirm="false" :alert-mode="true"
            :is-second-dialog="true" />
    </base-dialog>
</template>

<script>

import { db } from "@/utils/Firebase";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import SkeletonLoader from "@/components/common/SkeletonLoader";
import KimsaTagsPicker from '../../common/inputs/KimsaTagsPicker.vue';
import KimsaToggle from "@/components/common/inputs/KimsaToggle";
import BaseDialog from "@/components/common/BaseDialog";
import DialogActions from "@/components/common/DialogActions";
import { getQuestions } from "../../../utils/LocalData";
import draggable from 'vuedraggable';
import CreateQuestionDialog from './CreateQuestionDialog.vue';
import KimsaConfirm from '../../common/KimsaConfirm.vue';

export default {
    name: "CreateQuestionnaireDialog",
    props: {
        coachId: {
            type: String,
            default: null,
        },
    },
    components: {
        draggable,
        SkeletonLoader,
        KimsaTextField,
        KimsaTagsPicker,
        KimsaToggle,
        BaseDialog,
        DialogActions,
        CreateQuestionDialog,
        KimsaConfirm,
    },
    data() {
        return {
            dialog: false,
            editMode: false,
            questionnaireId: null,
            loadingOptions: false,
            loading: false,
            name: '',
            tags: [],
            questionOptions: [],
            readonly: false,
            hoverIndex: null,
            loadingDelete: false,
            deleteQuestion: null,
        }
    },
    computed: {
        disabledAdd(vm = this) {
            return !vm.name
        },
        title(vm = this) {
            if (vm.readonly) return vm.$t('questionnaires.basic')
            return vm.editMode ? vm.$t('questionnaires.edit_questionnaire') : vm.$t('questionnaires.create_questionnaire')
        }
    },
    methods: {
        async show() {
            const vm = this
            vm.dialog = true
            await vm.loadCoachQuestions()
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.editMode = false
            vm.questionnaireId = null
            setTimeout(() => {
                vm.reset()
            }, 200);
        },
        reset() {
            const vm = this
            vm.name = ''
            vm.tags = []
            vm.questionOptions = []
            vm.readonly = false
            vm.deleteQuestion = null
        },
        onMove(evt) {
            const draggedItem = evt.draggedContext?.element;
            const relatedItem = evt.relatedContext?.element;
            if (draggedItem?.force) return false;
            if (relatedItem?.force) return false;
            return true;
        },
        async loadCoachQuestions() {
            const vm = this;

            if (vm.readonly) {
                vm.questionOptions = getQuestions().slice(0, 5).map(question => ({ ...question, added: true }))
                return;
            }

            await vm.withLoading(async () => {
                let questionOptions = getQuestions()
                let questionsRef = await db.collection('questions').where('coachId', '==', vm.coachId).get()
                questionsRef.docs.forEach(doc => {
                    questionOptions.push({ id: doc.id, ...doc.data() })
                });
                vm.questionOptions = questionOptions.map((question, i) => {
                    return {
                        ...question,
                        added: [0, 1, 2, 3, 4].includes(i)
                    }
                })
            }, 'loadingOptions')
        },
        async setEditingMode(questionnaire) {
            const vm = this
            vm.editMode = true
            vm.readonly = questionnaire?.readonly || false
            await vm.show()

            vm.questionnaireId = questionnaire.objectID || questionnaire.id
            vm.name = questionnaire.name
            vm.tags = questionnaire.tags || []

            let questionnaireQuestions = questionnaire.questions
            if (!questionnaireQuestions && !vm.readonly && vm.questionnaireId) {
                const ref = await db.collection('questionnaires').doc(vm.questionnaireId).get()
                questionnaireQuestions = ref.data()?.questions || []
            }
            let questionsIds = (questionnaireQuestions || []).map(question => question.id)
            vm.questionOptions = vm.questionOptions.map(question => {
                if (question.force) {
                    const forcedIdx = getQuestions().findIndex(q => q.id === question.id)
                    return { ...question, added: true, order: forcedIdx }
                }
                const index = questionsIds.indexOf(question.id)
                return {
                    ...question,
                    added: questionsIds.includes(question.id),
                    order: index !== -1 ? 1000 + index : 9999
                }
            }).sort((a, b) => a.order - b.order)
        },
        async save() {
            const vm = this
            await vm.withLoading(async () => {
                let payload = {
                    name: vm.name,
                    tags: vm.tags,
                    questions: vm.questionOptions.filter(question => question.added),
                    coachId: vm.coachId,
                }

                let objectID = vm.questionnaireId
                if (vm.editMode) {
                    await db.collection('questionnaires').doc(vm.questionnaireId).update(payload)
                } else {
                    const created = await db.collection('questionnaires').add(payload)
                    objectID = created.id
                }

                vm.$emit('success', { objectID, ...payload })
                vm.toast(vm.$t('questionnaires.saved'))
                vm.hide()
            })
        },
        showAddQuestion() {
            const vm = this;
            vm.$refs.createQuestion.show()
        },
        onSaveQuestion(question) {
            const vm = this;
            vm.questionOptions.push(question)
        },
        showDeleteQuestion(question, index) {
            const vm = this;
            vm.deleteQuestion = { ...question, index }
            vm.$refs.showDelete.show()
        },
        async onDeleteQuestion() {
            const vm = this;
            await vm.withLoading(async () => {
                await db.collection('questions').doc(vm.deleteQuestion.id).delete()
                vm.questionOptions.splice(vm.deleteQuestion.index, 1);
                vm.hoverIndex = null
                vm.deleteQuestion = null
                vm.$refs.showDelete?.hide()
                vm.toast(vm.$t('questions.deleted'))
            }, 'loadingDelete')
        }
    },

}
</script>

<style scoped>
.ques-body {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.ques-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    color: var(--v-txtSecondary-base);
    text-transform: uppercase;
    letter-spacing: .04em;
    margin-bottom: 6px;
}

.q-list {
    display: flex;
    flex-direction: column;
}

.q-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 10px 10px 4px;
    border: 1px solid transparent;
    border-bottom: 1px solid var(--v-outline-base);
    border-radius: 8px;
    transition: background .15s, border-color .15s;
}

.q-row:last-child {
    border-bottom: 1px solid transparent;
}

.q-row:hover {
    background: var(--v-surface2-base);
    border-color: var(--v-outline-base);
}

.q-row--fixed:hover {
    background: transparent;
    border-color: transparent;
    border-bottom-color: var(--v-outline-base);
}

.q-drag {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    flex-shrink: 0;
    color: var(--v-txtMuted-base);
    cursor: grab;
    transition: color .15s;
}

.q-drag:active {
    cursor: grabbing;
}

.q-row:hover .q-drag {
    color: var(--v-accent-base);
}

.q-drag .v-icon {
    color: inherit !important;
}

.q-drag.hidden {
    visibility: hidden;
}

.q-ghost {
    opacity: .9;
    background: rgba(51, 153, 153, .08);
    border: 1px dashed var(--v-accent-base) !important;
    border-radius: 8px;
}

.q-ghost .q-text,
.q-ghost .q-drag {
    opacity: 0;
}

.q-chosen {
    background: var(--v-surface2-base);
    border-color: var(--v-outline-base);
}

.q-dragging {
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline2-base) !important;
    border-radius: 8px;
    box-shadow: 0 10px 28px rgba(0, 0, 0, .35);
}

.q-text {
    flex: 1;
    font-size: 13px;
    color: var(--v-txtPrimary-base);
}

.q-del {
    width: 26px;
    height: 26px;
    border-radius: 6px;
    background: rgba(232, 92, 92, .12);
    border: 1px solid rgba(232, 92, 92, .3);
    color: var(--v-error-base);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
}

.q-del .v-icon {
    color: inherit !important;
}

.q-add {
    width: 36px !important;
    height: 36px !important;
    margin-top: 14px;
    border-radius: 50% !important;
    background: var(--v-surface2-base) !important;
    border: 1px solid var(--v-outline2-base) !important;
    color: var(--v-txtPrimary-base) !important;
}

.q-add:hover {
    border-color: var(--v-accent-base) !important;
    color: var(--v-accent-base) !important;
}

.q-add .v-icon {
    color: inherit !important;
}
</style>